import { create } from 'zustand';
import { projectsApi } from '../services/api';
import { PROJECT_STATUSES } from '../constants/projectStatuses';
import { CLIENT_PARTY_TYPES } from '../constants/clientPartyTypes';
import { PROJECT_SCOPE_TYPES } from '../constants/projectScopeTypes';

// 1. Central initial state for easy reset
const initialState = {
  // Data
  projects: [],
  isLoading: false,
  error: null,

  // Filters (matching ProjectsManagement component)
  searchQuery: '',
  selectedBusinessAreas: [],
  selectedClients: [],
  selectedBusinessUnits: [],
  selectedProjectStatus: [PROJECT_STATUSES.ACTIVE],
  selectedCluster: '',
  selectedClientPartyTypes: [CLIENT_PARTY_TYPES.EXTERNAL, CLIENT_PARTY_TYPES.INTERNAL],
  selectedScopeTypes: [PROJECT_SCOPE_TYPES.LOCAL, PROJECT_SCOPE_TYPES.GLOBAL],
  selectedManager: null,
  includeHierarchyProjects: false,
  accessibleProjectIds: null,
  isLoadingAccessibleProjects: false,

  // UI State for persistence
  scrollPosition: 0,
  lastCardId: null,
  isReturningFromDetails: false,
  uiSearchInput: '',
};

const useProjectsStoreRepository = create((set, get) => {
  // 2. Fetch all projects (non-paginated)
  const fetchProjects = async () => {
    if (get().isLoading) {
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      const projects = await projectsApi.getAllProjects();

      set({
        projects: projects || [],
        isLoading: false,
        error: null,
      });
      return projects;
    } catch (err) {
      set({
        error: err.message || 'Failed to load projects',
        isLoading: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setScrollPosition: (position) => set({ scrollPosition: position }),
    setLastCardId: (id) => set({ lastCardId: id }),
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setUISearchInput: (input) => set({ uiSearchInput: input }),

    // Filter setters (update store filters - no refetch needed since filtering is client-side)
    setSearchQuery: (query) => set({ searchQuery: query }),
    setSelectedBusinessAreas: (areas) => set({ selectedBusinessAreas: areas }),
    setSelectedClients: (clients) => set({ selectedClients: clients }),
    setSelectedBusinessUnits: (units) => set({ selectedBusinessUnits: units }),
    setSelectedProjectStatus: (statuses) => set({ selectedProjectStatus: statuses }),
    setSelectedCluster: (cluster) => set({ selectedCluster: cluster }),
    setSelectedClientPartyTypes: (types) => set({ selectedClientPartyTypes: types }),
    setSelectedScopeTypes: (types) => set({ selectedScopeTypes: types }),
    setSelectedManager: (manager) => set({ selectedManager: manager }),
    setIncludeHierarchyProjects: (value) => set({ includeHierarchyProjects: value }),
    setAccessibleProjectIds: (ids) => set({ accessibleProjectIds: ids }),
    setIsLoadingAccessibleProjects: (loading) => set({ isLoadingAccessibleProjects: loading }),

    // Data actions
    fetchProjects,

    updateProject: (updatedProject) => {
      if (!updatedProject || !updatedProject.id) {
        return false;
      }
      const { projects } = get();
      const index = projects.findIndex(proj => proj.id === updatedProject.id);

      if (index !== -1) {
        // Update existing project
        const updatedProjects = [...projects];
        updatedProjects[index] = updatedProject;
        set({ projects: updatedProjects });
        return true;
      } else {
        // Add new project at the beginning
        set({
          projects: [updatedProject, ...projects]
        });
        return true;
      }
    },

    removeProject: (projectId) => {
      if (!projectId) return false;

      const { projects } = get();
      const index = projects.findIndex(proj => proj.id === projectId);

      if (index !== -1) {
        const updatedProjects = projects.filter(proj => proj.id !== projectId);
        set({ projects: updatedProjects });
        return true;
      }
      return false;
    },

    // Filters reset
    resetFilters: () => {
      set({
        searchQuery: '',
        uiSearchInput: '',
        selectedBusinessAreas: [],
        selectedClients: [],
        selectedBusinessUnits: [],
        selectedProjectStatus: [PROJECT_STATUSES.ACTIVE],
        selectedCluster: '',
        selectedClientPartyTypes: [CLIENT_PARTY_TYPES.EXTERNAL, CLIENT_PARTY_TYPES.INTERNAL],
        selectedScopeTypes: [PROJECT_SCOPE_TYPES.LOCAL, PROJECT_SCOPE_TYPES.GLOBAL],
        selectedManager: null,
        includeHierarchyProjects: false,
        accessibleProjectIds: null,
      });
    },

    // State reset
    resetState: () => {
      set({
        ...initialState,
        // No need to preserve anything - component stays mounted
      });
    },

    getProjectByIndex: (index) => {
      const { projects } = get();
      return projects[index] || null;
    },

    getProjectById: (projectId) => {
      const { projects } = get();
      return projects.find(proj => proj.id === projectId) || null;
    },
  };
});

export default useProjectsStoreRepository;
